import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Continuously monitors a microphone for audio level.
 * Opens the mic stream on mount (or when deviceId changes) so the user
 * can see feedback before they start recording.
 *
 * Returns the live MediaStream so callers (e.g. useAudioRecording) can
 * re-use the same stream instead of requesting a second one.
 */
export function useAudioLevel(deviceId?: string) {
  const [audioLevel, setAudioLevel] = useState(0);
  const [micConnected, setMicConnected] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setAudioLevel(0);
    setMicConnected(false);
  }, []);

  useEffect(() => {
    let mounted = true;

    const start = async () => {
      try {
        const constraints: MediaTrackConstraints = deviceId
          ? { deviceId: { exact: deviceId } }
          : {};
        const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        setMicConnected(true);

        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          if (!analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(dataArray);

          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const rms = sum / dataArray.length / 255;
          setAudioLevel(Math.min(1, rms * 3));

          animationFrameRef.current = requestAnimationFrame(tick);
        };

        animationFrameRef.current = requestAnimationFrame(tick);
      } catch {
        // Permission denied or no mic — silently degrade
        if (mounted) setMicConnected(false);
      }
    };

    start();

    return () => {
      mounted = false;
      cleanup();
    };
  }, [deviceId, cleanup]);

  return { audioLevel, micConnected, stream: streamRef };
}
